import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["language", "status"]
  static values = { url: String, preferenceUrl: String, languageLabels: Object }

  connect() {
    this.connected = true
    this.session = Symbol()
    this.requests = new Map()
    // Request/render state belongs to this connection, not Turbo's DOM snapshot.
    this.renderedSlots = new WeakMap()
    this.queue = []
    this.active = 0
    this.preferenceSave = Promise.resolve()
    this.observer = new MutationObserver(() => this.scan())
    this.observer.observe(this.element.querySelector("#messages"), { childList: true, subtree: true })
    this.scan()
  }

  disconnect() {
    this.connected = false
    this.observer?.disconnect()
  }

  changeLanguage() {
    const language = this.languageTarget.value
    this.statusTarget.textContent = ""
    this.scan()
    // Serialize preference writes so rapid selection changes cannot save an
    // older language after a newer one. Translation requests are independent.
    this.preferenceSave = this.preferenceSave.catch(() => {}).then(async () => {
      try {
        await this.post(this.preferenceUrlValue, { language }, "PATCH")
      } catch {
        if (this.connected && this.languageTarget.value === language) {
          this.statusTarget.textContent = "Could not save your language preference."
        }
      }
    })
  }

  scan() {
    if (!this.connected) return
    const session = this.session
    const language = this.languageTarget.value
    for (const slot of this.element.querySelectorAll("[data-translation-message-id]")) {
      const id = slot.dataset.translationMessageId
      const fingerprint = slot.dataset.translationFingerprint
      const key = `${id}:${fingerprint}:${language}`
      if (this.renderedSlots.get(slot) === key) continue
      this.renderedSlots.set(slot, key)
      slot.replaceChildren()
      slot.hidden = language === "original"
      if (slot.hidden) continue
      slot.setAttribute("role", "status")
      slot.textContent = "Translating…"
      slot.classList.add("text-text-sm", "text-gray-600", "px-3", "py-2")
      if (!this.requests.has(key)) {
        const request = this.enqueue(language, () => this.post(this.urlValue, { message_id: id, language }))
        this.requests.set(key, request)
        request.catch(() => {
          if (this.requests.get(key) === request) this.requests.delete(key)
        })
      }
      this.requests.get(key).then(result => {
        if (!this.current(slot, key, language, session)) return
        if (result.fingerprint && result.fingerprint !== fingerprint) throw new Error("Stale translation")
        this.render(slot, result, key)
      }).catch(() => {
        if (this.current(slot, key, language, session)) this.render(slot, { action: "error" }, key)
      })
    }
  }

  current(slot, key, language, session) {
    return this.connected && this.session === session && slot.isConnected && this.renderedSlots.get(slot) === key && this.languageTarget.value === language
  }

  enqueue(language, task) {
    return new Promise((resolve, reject) => {
      this.queue.push({ language, task, resolve, reject })
      this.pump()
    })
  }

  pump() {
    while (this.active < 2 && this.queue.length) {
      const item = this.queue.shift()
      if (!this.connected || item.language !== this.languageTarget.value) {
        item.reject(new Error("Cancelled"))
        continue
      }
      this.active++
      const session = this.session
      item.task().then(item.resolve, item.reject).finally(() => {
        if (session !== this.session) return
        this.active--
        this.pump()
      })
    }
  }

  async post(url, payload, method = "POST") {
    const response = await fetch(url, {
      method, credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify(payload)
    })
    if (!response.ok || response.redirected) throw new Error("Translation unavailable")
    return response.json()
  }

  render(slot, result, key) {
    slot.replaceChildren()
    slot.removeAttribute("role")
    slot.hidden = ["keep", "skip"].includes(result.action)
    if (slot.hidden) return
    if (result.action === "translate" && typeof result.text === "string" && result.text.trim()) {
      const details = document.createElement("details")
      details.open = true
      const summary = document.createElement("summary")
      summary.className = "cursor-pointer font-medium"
      summary.textContent = `${this.languageLabelsValue[this.languageTarget.value] || this.languageTarget.value} translation`
      const text = document.createElement("div")
      text.className = "mt-2 whitespace-pre-wrap break-words text-gray-900"
      text.setAttribute("dir", "auto")
      text.setAttribute("lang", result.target_language)
      text.textContent = result.text
      details.append(summary, text)
      slot.append(details)
      return
    }
    const label = document.createElement("span")
    label.textContent = result.action === "review" ? "Unable to confidently translate this message. " : "Translation unavailable. "
    const retry = document.createElement("button")
    retry.type = "button"
    retry.className = "underline font-medium"
    retry.textContent = "Retry"
    retry.addEventListener("click", () => {
      this.requests.delete(key)
      this.renderedSlots.delete(slot)
      this.scan()
    })
    slot.append(label, retry)
  }
}
